import React from 'react';
import { Link } from 'react-router-dom';
import { login } from '../services/auth.service';

import { useAuth } from '../services/useAuth';
import { Loading } from '../components/Loading';
import { MyHackathons } from './HackathonPage/MyHackathons';
import Container from '@mui/material/Container/Container';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import { alpha, useTheme } from '@mui/material/styles';
import { LOOK } from '../theme';
import LockIcon from '@mui/icons-material/Lock';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import GroupsIcon from '@mui/icons-material/Groups';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PaletteIcon from '@mui/icons-material/Palette';
import { telemetry } from '../services/persera-telemetry-browser';

export default function Home() {
    const { user, loading } = useAuth();

    if (loading) {
        return <Loading />;
    }

    if (!user) {
        return <Landing />;
    }

    return <MyHackathons />;
}

const features = [
    { icon: <LockIcon />, title: 'Private by default', body: 'Invite codes and email-domain restrictions keep your event to colleagues, friends, whomever.' },
    { icon: <LightbulbIcon />, title: 'Idea submission', body: 'Participants pitch ideas with a template you control, before the deadline you set.' },
    { icon: <GroupsIcon />, title: 'Teams form naturally', body: 'People join the ideas they like. Team-size limits are enforced for you.' },
    { icon: <HowToVoteIcon />, title: 'Voting, simplified', body: 'Pick the categories, choose judges, and we tally the scores. No more spreadsheets.' },
    { icon: <SlideshowIcon />, title: 'Presentation day', body: 'Order presenters, show who is up next, and open live voting for each demo.' },
    { icon: <PaletteIcon />, title: 'Your brand', body: 'Add your logo, colors and banner so the hackathon feels like part of your company.' }
];

const steps = [
    { title: 'Create', body: 'Set dates, team sizes and judging categories in a few minutes.' },
    { title: 'Share', body: 'Send the link or QR code. People sign in, pitch ideas and form teams.' },
    { title: 'Judge', body: 'Run presentations, open voting and announce the winners.' }
];

// the funnel's first step (landing_view), counted on the server once per browser session. The count carries no id, so
// it doesn't wait for cookie consent; following this visitor further (telemetry.track) does.
const countLandingView = () => {
    try {
        if (sessionStorage.getItem('hiab.landingCounted')) {
            return;
        }
        sessionStorage.setItem('hiab.landingCounted', '1');
    } catch (e) {
        // storage blocked: count it anyway, a repeat is better than a gap
    }
    fetch('/api/funnel/landing', { method: 'POST', keepalive: true }).catch(() => undefined);
    telemetry.track('landing_view');
};

// a small product card floating over the sky, like the app's own
const FloatingCard = ({ children, sx }: { children: React.ReactNode, sx?: object }) => (
    <Box sx={{
        position: 'absolute',
        display: { xs: 'none', md: 'block' },
        px: 2.25, py: 1.75,
        borderRadius: '18px',
        backgroundColor: alpha(LOOK.paper, 0.78),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha('#ffffff', 0.7)}`,
        boxShadow: `0 24px 48px -16px ${alpha(LOOK.terminal, 0.28)}`,
        ...sx
    }}>
        {children}
    </Box>
);

const Landing = () => {
    const theme = useTheme();
    React.useEffect(countLandingView, []);

    return (
        <Stack spacing={{ xs: 8, md: 14 }} useFlexGap sx={{ pb: { xs: 2, md: 4 } }}>
            {/* the hero: full width, the sky running up under the header */}
            <Box sx={{
                mt: { xs: -2, md: -4 },
                background: `${LOOK.grain}, ${LOOK.hero}`,
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Container sx={{ position: 'relative', minHeight: { md: 'calc(100vh - 64px)' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: { xs: 7, md: 10 } }}>
                    <Box sx={{ alignSelf: { md: 'center' }, mb: { xs: 3, md: 5 } }}>
                        <Box component="span" sx={{ display: 'inline-block', border: `1px solid ${alpha(LOOK.ink, 0.6)}`, borderRadius: 999, px: 1.75, py: 0.6, fontFamily: theme.brand.mono, fontSize: '0.9rem' }}>
                            The hackathon platform for companies
                        </Box>
                    </Box>
                    <Typography variant="h1" sx={{ fontSize: { xs: '3.25rem', sm: '4.5rem', md: '6.5rem' }, lineHeight: 0.95, letterSpacing: '-0.04em' }}>
                        <Box component="span" sx={{ display: 'block', pl: { md: '8%' } }}>Hackathons,</Box>
                        <Box component="span" sx={{ display: 'block', pl: { md: '34%' } }}>
                            without the spreadsheets
                            {/* a terminal cursor, in the icon's green; still for people who ask for less motion */}
                            <Box component="span" aria-hidden sx={{
                                display: 'inline-block', width: '0.45em', height: '0.78em', ml: '0.1em', verticalAlign: '-0.04em',
                                backgroundColor: LOOK.green, animation: 'hiab-blink 1.1s steps(1) infinite',
                                '@keyframes hiab-blink': { '50%': { opacity: 0 } },
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
                            }} />
                        </Box>
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mt: { xs: 4, md: 7 } }}>
                        <Box sx={{ gridColumn: { md: 2 } }}>
                            <Stack spacing={3}>
                                <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.4rem' }, lineHeight: 1.45, maxWidth: 520 }}>
                                    Ideas, teams, judging and presentation day in one place, so you can run the event instead of the admin.
                                </Typography>
                                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                    <Button variant="contained" size="large" component={Link} to="/new">Get started free</Button>
                                    <Button size="large" component={Link} to="/how/to" sx={{ backgroundColor: LOOK.kraft, color: LOOK.kraftInk, '&:hover': { backgroundColor: alpha(LOOK.kraft, 0.85) } }}>See how it works</Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Box>

                    <FloatingCard sx={{ top: '16%', right: '4%', transform: 'rotate(2deg)' }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#e0913f', boxShadow: '0 0 10px 2px rgba(224,145,63,0.5)' }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>Voting live</Typography>
                                <Typography variant="caption" color="text.secondary">12 ideas · 4 categories</Typography>
                            </Box>
                        </Stack>
                    </FloatingCard>
                    <FloatingCard sx={{ bottom: '18%', left: '3%', transform: 'rotate(-3deg)', width: 260 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>Team 3/3</Typography>
                        <Typography variant="h5" component="p" sx={{ mb: 0.5 }}>Lantern Linter</Typography>
                        <Typography variant="body2" color="text.secondary">Catches stale feature flags before they ship.</Typography>
                    </FloatingCard>
                </Container>
            </Box>

            {/* what it does: an editorial list, not a grid of tiles */}
            <Container>
                <Box sx={{ display: 'grid', gap: { xs: 4, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' } }}>
                    <Stack spacing={2} sx={{ position: { md: 'sticky' }, top: { md: 120 }, alignSelf: 'start' }}>
                        <Typography variant="overline" color="text.secondary">The product</Typography>
                        <Typography variant="h2" sx={{ fontSize: { xs: '2.25rem', md: '3.25rem' } }}>Everything a hackathon needs, nothing it doesn't</Typography>
                    </Stack>
                    <Box>
                        {features.map(feature => (
                            <Stack key={feature.title} direction="row" spacing={2.5} sx={{ py: 3, borderTop: `1px solid ${alpha(LOOK.ink, 0.15)}` }}>
                                <Box sx={{ color: LOOK.ink, pt: 0.5, '& svg': { fontSize: 26 } }}>{feature.icon}</Box>
                                <Box>
                                    <Typography variant="h4" component="h3" sx={{ mb: 0.75 }}>{feature.title}</Typography>
                                    <Typography color="text.secondary">{feature.body}</Typography>
                                </Box>
                            </Stack>
                        ))}
                    </Box>
                </Box>
            </Container>

            {/* how it works: three steps, numbered large */}
            <Container>
                <Typography variant="overline" color="text.secondary" component="p" sx={{ mb: 3 }}>How it works</Typography>
                <Box sx={{ display: 'grid', gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                    {steps.map((step, index) => (
                        <Box key={step.title}>
                            {/* the number on its own tinted tile */}
                            <Box sx={{
                                width: { xs: 88, md: 112 }, height: { xs: 88, md: 112 }, mb: 2.5, borderRadius: '24px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: `${LOOK.grain}, ${LOOK.heroPanel}`, boxShadow: `0 16px 32px -20px ${alpha(LOOK.terminal, 0.4)}`
                            }}>
                                <Typography variant="h1" component="span" sx={{ fontFamily: theme.brand.mono, fontWeight: 600, fontSize: { xs: '3rem', md: '4rem' }, lineHeight: 1 }}>{index + 1}</Typography>
                            </Box>
                            <Typography variant="h3" component="h3" sx={{ mb: 1 }}>{step.title}</Typography>
                            <Typography color="text.secondary">{step.body}</Typography>
                        </Box>
                    ))}
                </Box>
            </Container>

            {/* presentation day, live: the product's moment, navy with a mint glow */}
            <Container>
                <Box sx={{
                    borderRadius: { xs: '24px', md: '32px' },
                    color: LOOK.cream,
                    background: `radial-gradient(90% 120% at 85% 10%, ${alpha(LOOK.green, 0.2)}, transparent 55%), ${LOOK.terminal}`,
                    px: { xs: 3, md: 7 }, py: { xs: 5, md: 8 },
                    display: 'grid', gap: { xs: 4, md: 8 },
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    alignItems: 'center'
                }}>
                    <Stack spacing={2}>
                        <Typography variant="overline" sx={{ color: alpha(LOOK.cream, 0.6) }}>Presentation day</Typography>
                        <Typography variant="h2" sx={{ color: LOOK.cream, fontSize: { xs: '2.25rem', md: '3.25rem' } }}>The room runs itself</Typography>
                        <Typography sx={{ color: alpha(LOOK.cream, 0.75), maxWidth: 460 }}>
                            Teams see who's up next. Judges score each demo as it ends. Results add themselves up while you hand out the prizes.
                        </Typography>
                    </Stack>
                    <Stack spacing={1.25}>
                        {[
                            { label: 'Now presenting', title: 'Lantern Linter', live: true },
                            { label: 'Up next', title: 'Pipeline Doctor' },
                            { label: 'Then', title: 'Meeting Debt' }
                        ].map(row => (
                            <Box key={row.title} sx={{
                                borderRadius: '16px', px: 2.5, py: 1.75,
                                border: `1px solid ${alpha(LOOK.green, row.live ? 0.55 : 0.15)}`,
                                backgroundColor: alpha('#ffffff', row.live ? 0.06 : 0.02),
                                boxShadow: row.live ? `0 0 32px -6px ${alpha(LOOK.green, 0.45)}` : 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2
                            }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: alpha(LOOK.cream, 0.6), letterSpacing: '0.06em', textTransform: 'uppercase' }}>{row.label}</Typography>
                                    <Typography variant="h4" component="p" sx={{ color: LOOK.cream }}>{row.title}</Typography>
                                </Box>
                                {row.live && <Box sx={{ borderRadius: 999, px: 1.5, py: 0.5, fontSize: '0.85rem', color: LOOK.terminal, backgroundColor: LOOK.green, fontWeight: 600 }}>Voting open</Box>}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Container>

            {/* the close: back to the sky */}
            <Container>
                <Box sx={{
                    borderRadius: { xs: '24px', md: '32px' },
                    background: `${LOOK.grain}, ${LOOK.heroPanel}`,
                    px: { xs: 3, md: 7 }, py: { xs: 6, md: 9 },
                    textAlign: 'center'
                }}>
                    <Typography variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '4.5rem' }, lineHeight: 0.95, mb: 2 }}>Ready to hack?</Typography>
                    <Typography sx={{ fontSize: '1.2rem', mb: 4 }}>
                        Set up your event in minutes. It's free; <Link to="/pricing">pay per event</Link> only for your own branding.
                    </Typography>
                    <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <Button variant="contained" size="large" component={Link} to="/new">Get started free</Button>
                        <Button size="large" onClick={() => login()}>Sign in</Button>
                    </Stack>
                </Box>
            </Container>
        </Stack>
    )
}
